import AnalyticsCrossProductInteractionClient, {
	URL_QUERY_PARAM_INTERACTION_SESSION,
} from '@atlassiansox/analytics-cross-product-interaction-client';

import { fg } from '@atlaskit/platform-feature-flags';

import { type InteractionSession } from './types';
import urlParamExtractor from './urlUtils';

const isCrossProductInteractionAttribute = (_: any, paramName: string) => {
	return paramName === URL_QUERY_PARAM_INTERACTION_SESSION;
};

/**
 * This class initialises the Analytics Cross Product Interaction Client, which keeps track of the
 * interactionSessionId we use to track cross-product user journeys.
 *
 * It parses URL query parameters for any previous interaction session attributes.
 * If none exist, the Analytics Cross Product Interaction Client will generate a new
 * interactionSessionId, and the class will expose a method for retrieving this from Session Storage
 *
 * @param sendEventCallback: Expects a callback function that can send an analytics event
 */
export default class CrossProductInteractionTracking {
	private analyticsCrossProductInteractionClient?: AnalyticsCrossProductInteractionClient;
	private featureEnabled: boolean;

	constructor(sendEventCallback: Function) {
		this.featureEnabled = fg('analytics_cross_product_interaction_tracking');

		if (this.featureEnabled) {
			let bridge;
			let interactionSessionId;
			let interactionSource;

			const crossProductUrlPramater = urlParamExtractor(isCrossProductInteractionAttribute);
			const interactionSessionBlob =
				URL_QUERY_PARAM_INTERACTION_SESSION in crossProductUrlPramater
					? crossProductUrlPramater[URL_QUERY_PARAM_INTERACTION_SESSION]
					: null;

			let interactionSession;
			if (interactionSessionBlob) {
				try {
					interactionSession = JSON.parse(atob(interactionSessionBlob));
				} catch {
					// Gracefully fail through if parameter has been corrupted
					// TODO: Send analytics event
				} finally {
					if (interactionSession) {
						bridge = interactionSession.bridge ?? undefined;
						interactionSessionId = interactionSession.id ?? undefined;
						interactionSource = interactionSession.source ?? undefined;
					}
				}
			}

			const interactionSessionOptions =
				interactionSessionId || sendEventCallback
					? {
							providedSessionId: interactionSessionId || undefined,
							sendEventCallback: sendEventCallback || undefined,
						}
					: undefined;

			// Initialise interaction session client with parsed values if they exist
			this.analyticsCrossProductInteractionClient = new AnalyticsCrossProductInteractionClient(
				interactionSessionOptions,
				bridge,
				interactionSource,
			);
		}
	}

	getInteractionSession(): InteractionSession {
		if (this.featureEnabled && this.analyticsCrossProductInteractionClient) {
			return {
				bridge: this.analyticsCrossProductInteractionClient.getBridge(),
				id: this.analyticsCrossProductInteractionClient.getCurrentInteractionSessionId(),
				prevId: this.analyticsCrossProductInteractionClient.getPreviousInteractionSessionId(),
				source: this.analyticsCrossProductInteractionClient.getInteractionSource(),
			};
		} else {
			return {
				bridge: null,
				id: null,
				prevId: null,
				source: null,
			};
		}
	}
}
